/**
 * FeatureGroupCard Component
 *
 * Displays hooks within a feature group in a compact grid layout.
 * Shows dependencies between hooks visually.
 *
 * Features:
 * - Grid layout for wide screens (2-3 columns)
 * - Compact hook cards with toggle and description
 * - Visual dependency indicators (requires/dependents)
 * - [配置] button for hooks with detailed configuration tabs
 */

import React, { useMemo } from 'react';
import {
  ChevronRight,
  Package,
  Brain,
  Sparkles,
  Heart,
  Zap,
  BookOpen,
  Settings,
  Database,
  Star,
  Filter,
  Users,
  Bug,
  Clock,
  Save,
  UserCircle,
  Search,
  Repeat,
  Activity,
  Layers,
  MessageSquare,
  Link,
  ArrowRight,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  type FeatureGroup,
  type HookDefinition,
  type HookId,
  getHooksByGroup,
  isHookEnabled,
  getToggleValue,
  checkCanEnable,
  checkCanDisable,
  HOOKS_REGISTRY,
} from '@/config/hooks-registry';
import { getConfigValue } from '@/config/fieldConfig';
import type { AgentConfiguration } from '@/types';
import type { TabId } from '@/config/tabConfig';

// Icon mapping - extended with new icons
const ICONS: Record<string, LucideIcon> = {
  Package,
  Brain,
  Sparkles,
  Heart,
  Zap,
  BookOpen,
  Settings,
  Database,
  Star,
  Filter,
  Users,
  Bug,
  Clock,
  Save,
  UserCircle,
  Search,
  Repeat,
  Activity,
  Layers,
  MessageSquare,
  MapPin,
};


interface FeatureGroupCardProps {
  group: FeatureGroup;
  config: Partial<AgentConfiguration>;
  /** Update config value */
  onConfigChange: (path: string, value: unknown) => void;
  /** Navigate to a configuration tab, optionally to a specific section */
  onNavigateToTab?: (tabId: TabId, sectionId?: string) => void;
  /** App context with model lists for auto-selection */
  appContext?: Record<string, unknown>;
  /** Class name for styling */
  className?: string;
}

export const FeatureGroupCard: React.FC<FeatureGroupCardProps> = ({
  group,
  config,
  onConfigChange,
  onNavigateToTab,
  className = '',
}) => {
  const { messages } = useI18n();
  const t = messages.featureGroups;

  // Get hooks for this group
  const hooks = useMemo(() => getHooksByGroup(group.id), [group.id]);

  // Calculate enabled hooks set for dependency checking
  const enabledHooks = useMemo(() => {
    const set = new Set<HookId>();
    for (const hook of Object.values(HOOKS_REGISTRY)) {
      const value = getConfigValue(config, hook.configPath);
      if (isHookEnabled(hook.id, value)) {
        set.add(hook.id);
      }
    }
    return set;
  }, [config]);

  // Count enabled hooks in this group
  const enabledCount = hooks.filter((h) => {
    const value = getConfigValue(config, h.configPath);
    return isHookEnabled(h.id, value);
  }).length;

  // Get group icon
  const GroupIcon = ICONS[group.icon] || Package;

  // Handle hook toggle
  const handleHookToggle = (hook: HookDefinition) => {
    const currentValue = getConfigValue(config, hook.configPath);
    const currentEnabled = isHookEnabled(hook.id, currentValue);

    if (currentEnabled) {
      // Disabling: check dependencies first
      const { canDisable, blockedBy } = checkCanDisable(hook.id, enabledHooks);
      if (!canDisable) {
        const blockedNames = blockedBy.map(id => HOOKS_REGISTRY[id]?.displayName || id).join('、');
        alert(`无法禁用：「${blockedNames}」依赖此功能`);
        return;
      }
      // Disable: set to null/false
      const newValue = getToggleValue(hook.id, false);
      onConfigChange(hook.configPath, newValue);
    } else {
      // Enabling: check dependencies
      const { canEnable, missingDeps } = checkCanEnable(hook.id, enabledHooks);
      if (!canEnable) {
        const missingNames = missingDeps.map(id => HOOKS_REGISTRY[id]?.displayName || id).join('、');
        const confirmMsg = (t?.confirmEnableDependencies || 'Need to enable "{dependencies}" first. Enable them together?').replace('{dependencies}', missingNames);
        if (!confirm(confirmMsg)) {
          return;
        }
        // Enable dependencies first
        for (const depId of missingDeps) {
          const depHook = HOOKS_REGISTRY[depId];
          if (depHook) {
            const depValue = getToggleValue(depId, true);
            onConfigChange(depHook.configPath, depValue);
          }
        }
      }

      // Enable the hook
      const newValue = getToggleValue(hook.id, true);
      onConfigChange(hook.configPath, newValue);
    }
  };

  // Render a single hook card
  const renderHookCard = (hook: HookDefinition) => {
    const value = getConfigValue(config, hook.configPath);
    const isEnabled = isHookEnabled(hook.id, value);
    const HookIcon = hook.icon ? ICONS[hook.icon] : undefined;
    const { canEnable } = checkCanEnable(hook.id, enabledHooks);
    const hasDeps = hook.requires && hook.requires.length > 0;
    const hasDependents = hook.dependents && hook.dependents.length > 0;

    return (
      <div
        key={hook.id}
        className={`
          relative p-3 rounded-lg border transition-all
          ${isEnabled
            ? 'bg-white border-green-200 shadow-sm'
            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
          }
        `}
      >
        {/* Header: Icon + Toggle */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {HookIcon && (
              <div className={`p-1.5 rounded ${isEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                <HookIcon className="w-4 h-4" />
              </div>
            )}
            <span className={`font-medium text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>
              {hook.displayName}
            </span>
          </div>

          {/* Toggle Button */}
          <button
            type="button"
            onClick={() => handleHookToggle(hook)}
            className={`
              relative w-10 h-5 rounded-full transition-colors
              ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}
            `}
          >
            <div className={`
              absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
              ${isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
            `} />
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 leading-relaxed mb-2">
          {hook.description}
        </p>

        {/* Footer: Tags + Config Button */}
        <div className="flex items-center justify-between">
          {/* Tags: enhancesPrompt + Dependencies */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {hook.enhancesPrompt && (
              <span
                className="text-gray-400"
                title="启用后会注入上下文信息到系统提示词"
              >
                增强提示词
              </span>
            )}
            {hasDeps && (
              <span
                className={`flex items-center gap-1 ${!canEnable && !isEnabled ? 'text-amber-600' : 'text-gray-400'}`}
                title={`依赖: ${hook.requires?.map(id => HOOKS_REGISTRY[id]?.displayName).join('、')}`}
              >
                <Link className="w-3 h-3" />
                <span>依赖</span>
              </span>
            )}
            {hasDependents && isEnabled && (
              <span
                className="flex items-center gap-1 text-blue-500"
                title={`被依赖: ${hook.dependents?.map(id => HOOKS_REGISTRY[id]?.displayName).join('、')}`}
              >
                <ArrowRight className="w-3 h-3" />
                <span>被依赖</span>
              </span>
            )}
          </div>

          {/* Config button - show when active (enabled or expanded) */}
          {hook.linkedTab && isEnabled && onNavigateToTab && (
            <button
              type="button"
              onClick={() => onNavigateToTab(hook.linkedTab!, hook.linkedSection)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              <span>配置</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${enabledCount > 0 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
            <GroupIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{group.name}</h3>
              {group.alwaysEnabled && (
                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">
                  始终启用
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">{group.description}</p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {enabledCount}/{hooks.length}
        </div>
      </div>

      {/* Hooks Grid */}
      <div className="p-4">
        {hooks.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">暂无可配置项</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {hooks.map(renderHookCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureGroupCard;
